import React, { useState } from 'react';
import { Flame, AlertTriangle } from 'lucide-react';
import { Card } from './ui/card';
import { Slider } from './ui/slider';

export const IntensityMeter = ({ pepperTypes }) => {
  const [selectedLevel, setSelectedLevel] = useState(5);

  const getCurrentPepper = () => {
    if (selectedLevel <= 3) return pepperTypes[3]; // Jalapeño
    if (selectedLevel <= 6) return pepperTypes[2]; // Habanero
    if (selectedLevel <= 8) return pepperTypes[1]; // Trinidad Scorpion
    return pepperTypes[0]; // Carolina Reaper
  };

  const currentPepper = getCurrentPepper();

  const getWarningLevel = () => {
    if (selectedLevel <= 3) return { color: 'text-green-500', bg: 'bg-green-950/50', border: 'border-green-600', message: 'Iniciante - Você consegue!' };
    if (selectedLevel <= 6) return { color: 'text-yellow-500', bg: 'bg-yellow-950/50', border: 'border-yellow-600', message: 'Intermediário - Cuidado!' };
    if (selectedLevel <= 8) return { color: 'text-orange-500', bg: 'bg-orange-950/50', border: 'border-orange-600', message: 'Avançado - Muito Quente!' };
    return { color: 'text-red-500', bg: 'bg-red-950/50', border: 'border-red-600', message: 'EXTREMO - SÓ PARA CORAJOSOS!' };
  };

  const warning = getWarningLevel();

  return (
    <section id="intensity" className="relative py-24 bg-gradient-to-b from-black via-zinc-900 to-black">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-3 mb-4">
            <div className="h-px w-12 bg-red-600"></div>
            <AlertTriangle className="w-8 h-8 text-red-600" />
            <div className="h-px w-12 bg-red-600"></div>
          </div>
          <h2 className="text-5xl sm:text-6xl font-black text-white mb-6 tracking-tight">
            MEDIDOR DE INTENSIDADE
          </h2>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            Você tem coragem? Descubra qual nível de pimenta você aguenta!
          </p>
        </div>

        {/* Interactive Meter */}
        <div className="max-w-4xl mx-auto">
          <Card className="bg-gradient-to-br from-zinc-900 to-black border-2 border-red-600 p-8 sm:p-12">
            {/* Current Pepper Display */}
            <div className={`${warning.bg} border-2 ${warning.border} rounded-xl p-8 mb-10 transition-all duration-300`}>
              <div className="text-center mb-6">
                <h3 className="text-3xl sm:text-4xl font-black text-white mb-2">
                  {currentPepper.name}
                </h3>
                <p className={`text-xl ${warning.color} font-bold`}>
                  {currentPepper.scoville}
                </p>
              </div>

              {/* Flame Icons representing heat level */}
              <div className="flex items-center justify-center gap-2 mb-6">
                {[...Array(10)].map((_, index) => (
                  <Flame
                    key={index}
                    className={`w-6 h-6 transition-all duration-300 ${
                      index < currentPepper.level
                        ? 'text-red-600 animate-pulse'
                        : 'text-gray-700'
                    }`}
                    fill={index < currentPepper.level ? 'currentColor' : 'none'}
                  />
                ))}
              </div>

              {/* Warning Message */}
              <div className={`text-center ${warning.color} font-black text-xl tracking-wider`}>
                {warning.message}
              </div>
            </div>

            {/* Slider Control */}
            <div className="space-y-6">
              <div className="flex items-center justify-between mb-4">
                <span className="text-white font-bold">Selecione seu nível:</span>
                <div className="flex items-center gap-2">
                  <Flame className="w-6 h-6 text-red-600" />
                  <span className="text-3xl font-black text-white">{selectedLevel}</span>
                </div>
              </div>

              <Slider
                value={[selectedLevel]}
                onValueChange={(value) => setSelectedLevel(value[0])}
                min={1}
                max={10}
                step={1}
                className="cursor-pointer"
              />

              <div className="flex justify-between text-sm font-bold">
                <span className="text-green-500">1 - SUAVE</span>
                <span className="text-yellow-500">5 - MÉDIO</span>
                <span className="text-red-600">10 - INFERNO</span>
              </div>
            </div>

            {/* All Pepper Types Reference */}
            <div className="mt-12 pt-8 border-t-2 border-red-900/30">
              <h4 className="text-xl font-black text-white mb-6 text-center">
                ESCALA DE PIMENTAS
              </h4>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {pepperTypes.map((pepper, index) => (
                  <div
                    key={index}
                    className={`bg-zinc-800/50 border-2 ${
                      pepper.name === currentPepper.name ? 'border-red-600' : 'border-red-900/30'
                    } rounded-lg p-4 transition-all duration-300 hover:border-red-600`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Flame className={`w-5 h-5 ${
                        pepper.level >= 9 ? 'text-red-600' :
                        pepper.level >= 7 ? 'text-orange-500' :
                        pepper.level >= 5 ? 'text-yellow-500' :
                        'text-green-500'
                      }`} />
                      <span className="text-white font-bold text-sm">{pepper.name}</span>
                    </div>
                    <p className="text-xs text-gray-400">{pepper.scoville}</p>
                  </div>
                ))}
              </div>
            </div>
          </Card>
        </div>
      </div>
    </section>
  );
};
